import firebaseConfig from '../../firebase-applet-config.json';

declare global {
  interface Window {
    google?: any;
  }
}

// Singleton token client instance
let tokenClientInstance: any = null;
let currentResolve: ((value: { accessToken: string; expiresIn?: number }) => void) | null = null;
let currentReject: ((reason?: any) => void) | null = null;

/**
 * Initializes the Google Identity Services OAuth 2.0 Token Client once.
 * Strictly requests ONLY the read-only scope 'https://www.googleapis.com/auth/gmail.readonly'.
 * Does NOT request 'gmail.send' or any write permissions.
 * Never exposes or requires any Google client secret.
 */
export function getActiveOAuthClientId(): string {
  if (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_GOOGLE_OAUTH_CLIENT_ID) {
    return (import.meta as any).env.VITE_GOOGLE_OAUTH_CLIENT_ID;
  }
  return firebaseConfig.oAuthClientId || '';
}

export function initGisTokenClient(): boolean {
  if (tokenClientInstance) {
    return true;
  }

  const clientId = getActiveOAuthClientId();
  if (!clientId) {
    console.warn('OAuth Client ID is not configured in firebase-applet-config.json or VITE_GOOGLE_OAUTH_CLIENT_ID');
    return false;
  }

  if (!window.google?.accounts?.oauth2) {
    return false;
  }

  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    console.info(`[GIS OAuth] Initializing Token Client (Client ID: ${clientId}, Origin: ${origin}, Scope: https://www.googleapis.com/auth/gmail.readonly)`);

    tokenClientInstance = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: 'https://www.googleapis.com/auth/gmail.readonly',
      prompt: '',
      hint: 'naeembuilder48@gmail.com',
      callback: (response: any) => {
        if (response.error) {
          console.error('GIS OAuth Error Response:', response);
          if (response.error === 'access_denied') {
            currentReject?.(
              new Error('Authorization was cancelled or access was denied. Please approve read-only permissions for naeembuilder48@gmail.com.')
            );
          } else if (response.error === 'origin_mismatch' || response.error_description?.includes('origin_mismatch')) {
            currentReject?.(
              new Error(`Error 400: origin_mismatch. The current origin (${origin}) is not registered under 'Authorized JavaScript origins' for Client ID '${clientId}'.`)
            );
          } else {
            currentReject?.(
              new Error(response.error_description || response.error || 'Google Authorization Error')
            );
          }
          currentResolve = null;
          currentReject = null;
          return;
        }

        if (!response.access_token) {
          currentReject?.(new Error('No access token returned by Google Identity Services.'));
          currentResolve = null;
          currentReject = null;
          return;
        }

        currentResolve?.({
          accessToken: response.access_token,
          expiresIn: response.expires_in,
        });
        currentResolve = null;
        currentReject = null;
      },
      error_callback: (err: any) => {
        console.error('GIS Error Callback:', err);
        if (err?.type === 'popup_blocked') {
          currentReject?.(
            new Error(
              'Google sign-in popup was blocked by your browser. Please allow popups for this domain and click Connect Google Gmail again.'
            )
          );
        } else if (err?.type === 'popup_closed') {
          currentReject?.(
            new Error('Google sign-in popup was closed before authorization was completed.')
          );
        } else if (err?.type === 'origin_mismatch' || err?.message?.includes('origin_mismatch')) {
          currentReject?.(
            new Error(
              `Error 400: origin_mismatch. The current origin (${origin}) is not listed in Authorized JavaScript origins for Client ID '${clientId}'. Please add ${origin} to Authorized JavaScript origins in Google Cloud Console.`
            )
          );
        } else {
          currentReject?.(
            new Error(err?.message || 'Google Identity Services authentication error.')
          );
        }
        currentResolve = null;
        currentReject = null;
      },
    });
    return true;
  } catch (err: any) {
    console.error('Failed to initialize GIS Token Client:', err);
    return false;
  }
}

// Auto-initialize as soon as possible
if (typeof window !== 'undefined') {
  if (window.google?.accounts?.oauth2) {
    initGisTokenClient();
  } else {
    // Retry when DOM or window loads
    window.addEventListener('load', () => {
      initGisTokenClient();
    });
  }
}

/**
 * Invokes the Google Identity Services authorization request.
 * MUST be called directly from user click without intervening async delays
 * to ensure browsers do not block the popup window.
 */
export async function requestGisGmailToken(): Promise<{ accessToken: string; expiresIn?: number }> {
  return new Promise((resolve, reject) => {
    // Ensure token client is initialized
    if (!tokenClientInstance) {
      const initialized = initGisTokenClient();
      if (!initialized) {
        if (!window.google?.accounts?.oauth2) {
          return reject(
            new Error(
              'Google Identity Services library is still loading. Please ensure popups and third-party scripts are allowed, and click Connect Google Gmail again.'
            )
          );
        }
        return reject(new Error('Failed to initialize Google Identity Services token client.'));
      }
    }

    currentResolve = resolve;
    currentReject = reject;

    try {
      // Synchronously request access token within the click event execution context
      tokenClientInstance.requestAccessToken({
        prompt: 'consent',
        hint: 'naeembuilder48@gmail.com',
      });
    } catch (err: any) {
      currentResolve = null;
      currentReject = null;
      reject(err);
    }
  });
}
